import { useState } from "react";
import { toast } from "sonner";
import { Send, User, MapPin, Mail, Phone, Calendar, MessageSquare, Church } from "lucide-react";
import { api } from "@/lib/api";

const STUDIO = "/assets/studio-team.png";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", church_city: "", email: "", phone: "", desired_date: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact/invitation", form);
      toast.success(data.message || "Invitation envoyée");
      setForm({ name: "", church_city: "", email: "", phone: "", desired_date: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="relative bg-[#F7F5F0]">
      <section className="relative py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={STUDIO} alt="" className="w-full h-full object-cover jt-pan opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E4959]/90 via-[#4A6D82]/70 to-[#F7F5F0]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1]">Pasteurs & responsables</span>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] text-white mt-4 mb-5">
            Invite Jesus Talk <em className="not-italic text-[#F4E4C1]">chez toi</em>.
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mx-auto">
            Vous souhaitez nous accueillir pour prêcher, animer une soirée, ou intervenir lors d'un événement ? Écrivez-nous, nous répondrons personnellement.
          </p>
        </div>
      </section>

      <section className="pb-24 max-w-4xl mx-auto px-6 -mt-16 relative z-10">
        <form onSubmit={submit} data-testid="invitation-form" className="bg-white border border-[#DDE3E8] rounded-3xl p-6 md:p-10 jt-medium-shadow grid gap-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field icon={<User className="w-4 h-4" />} label="Votre nom" required>
              <input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Jean Dupont" className="w-full bg-transparent focus:outline-none text-[#1E2933]" />
            </Field>
            <Field icon={<Church className="w-4 h-4" />} label="Église / Ville" required>
              <input data-testid="contact-church" required value={form.church_city} onChange={set("church_city")} placeholder="Église Bethel, Paris" className="w-full bg-transparent focus:outline-none text-[#1E2933]" />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Field icon={<Mail className="w-4 h-4" />} label="Email" required>
              <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="pasteur@eglise.fr" className="w-full bg-transparent focus:outline-none text-[#1E2933]" />
            </Field>
            <Field icon={<Phone className="w-4 h-4" />} label="Téléphone (optionnel)">
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+33 6 12 34 56 78" className="w-full bg-transparent focus:outline-none text-[#1E2933]" />
            </Field>
          </div>
          <Field icon={<Calendar className="w-4 h-4" />} label="Date souhaitée">
            <input data-testid="contact-date" type="date" value={form.desired_date} onChange={set("desired_date")} className="w-full bg-transparent focus:outline-none text-[#1E2933]" />
          </Field>
          <Field icon={<MessageSquare className="w-4 h-4" />} label="Votre message" required multiline>
            <textarea data-testid="contact-message" required value={form.message} onChange={set("message")} rows={5}
              placeholder="Décrivez brièvement l'événement, le contexte de l'invitation, et vos attentes."
              className="w-full bg-transparent focus:outline-none text-[#1E2933] resize-none" />
          </Field>
          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <p className="text-xs text-[#6B7A85] max-w-md flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Nous intervenons en France, Belgique, Suisse, Afrique francophone et au-delà.
            </p>
            <button disabled={loading} type="submit" data-testid="contact-submit"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A6D82] text-white hover:bg-[#2E4959] transition disabled:opacity-60 jt-blue-glow">
              <Send className="w-4 h-4" />
              {loading ? "Envoi…" : "Envoyer l'invitation"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ icon, label, required, multiline, children }) {
  return (
    <label className="block rounded-2xl border border-[#DDE3E8] bg-[#F7F5F0] px-4 py-3 focus-within:border-[#4A6D82] focus-within:ring-2 focus-within:ring-[#4A6D82]/30 transition">
      <div className="flex items-center gap-2 text-xs text-[#6B7A85] mb-1.5">
        <span className="text-[#4A6D82]">{icon}</span>
        <span className="uppercase tracking-widest">{label}{required && <span className="text-[#C99A5B]"> *</span>}</span>
      </div>
      {children}
    </label>
  );
}
